import { Link, useLocation } from "react-router-dom";
import { SiankoMark } from "@/components/brand/Logo";
import { StreakChip } from "@/components/ui/StreakChip";
import { useAuth } from "@/lib/auth/context";
import { useCompletions } from "@/lib/hooks/useCompletions";
import { useStreakStatus } from "@/lib/hooks/useStreakStatus";
import {
  levelFromXp,
  levelTitle,
  totalXp,
  xpProgressInLevel,
} from "@/lib/utils/achievements";

export function AppHeader() {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const uid = firebaseUser?.uid;
  const isChild = userDoc?.role === "child";
  const { pathname } = useLocation();

  // Level liczone dla każdego (dziecko + rodzic) — cała rodzina gra
  // w XP. Rodzic dostaje XP za zatwierdzone completion (snapshot
  // kind=family + rewardMinor=0 robi createCompletion).
  const { completions: ownApproved } = useCompletions(hid, {
    status: "approved",
    completedBy: uid,
  });
  const xp = uid ? totalXp(ownApproved) : 0;
  const level = uid ? levelFromXp(xp) : null;
  const xpInfo = uid ? xpProgressInLevel(xp) : null;
  // Procent wypełnienia paska postępu w obrębie aktualnego poziomu.
  const xpPct = xpInfo && xpInfo.total > 0
    ? Math.min(100, Math.round((xpInfo.current / xpInfo.total) * 100))
    : 0;
  // Toggle: jeśli już na /stats, klik wraca na /dashboard.
  const onStatsPage = pathname.startsWith("/stats");
  const pillTarget = onStatsPage ? "/dashboard" : "/stats";

  // Streak chip pokazujemy tylko dziecku.
  const streakStatus = useStreakStatus(
    isChild ? hid : undefined,
    isChild ? uid : undefined,
  );

  return (
    <header
      className="sticky top-0 z-30 bg-cloud/85 backdrop-blur"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="mx-auto max-w-md h-14 px-4 flex items-center justify-between gap-3">
        <Link to="/dashboard" aria-label="Sianko" className="inline-flex items-center">
          <SiankoMark size={26} />
        </Link>
        <div className="flex items-center gap-2">
          {isChild && (
            <StreakChip
              days={streakStatus.streak}
              shield={
                streakStatus.shieldJustConsumed
                  ? "just-consumed"
                  : streakStatus.shieldAvailable
                    ? "available"
                    : "used"
              }
            />
          )}
          {/* Pill levelu — dziecko + rodzic. Klik toggle dashboard↔stats
              (powrót przez ten sam pill). Tło wypełnia się od lewej do
              prawej proporcjonalnie do XP w aktualnym poziomie — pełni
              funkcję paska postępu, widoczny zawsze w top barze. */}
          {level !== null && (
            <Link
              to={pillTarget}
              aria-label={
                onStatsPage
                  ? "Wróć do dashboardu"
                  : `Poziom ${level}, ${xpInfo?.current ?? 0} z ${xpInfo?.total ?? 0} XP — otwórz statystyki`
              }
            >
              <span className="relative inline-flex items-center gap-1.5 rounded-full h-7 px-3 text-[13px] font-extrabold bg-[#EAE6FE] text-[#5240B8] overflow-hidden cursor-pointer">
                {/* Pasek postępu — ciemniejszy fiolet od lewej, width = pct% */}
                <span
                  aria-hidden
                  className="absolute inset-y-0 left-0 bg-[#C7BCF7] transition-[width] duration-300 ease-out"
                  style={{ width: `${xpPct}%` }}
                />
                <span className="relative" aria-hidden>{levelTitle(level).emoji}</span>
                <span className="relative font-num">lv {level}</span>
              </span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
